import React, { useCallback, useEffect, useMemo } from 'react';
import styles from './UIGameTradeCreator.module.scss';
import clsx from 'clsx';
import { UIGameTradeCreatorProposal } from './UIGameTradeCreatorProposal/index.ts';
import { UIGamePlayerCardInventory } from '../UIGamePlayerCardInventory/index.ts';
import { UIGameTradeCreatorWantedCardSelector } from './UIGameTradeCreatorWantedCardSelector/index.ts';
import { CardEnum } from '@colonist/game-states';
import { TradeCreationDataValidator, BankValidator, TradeUtils } from '@colonist/game-mechanic-validators';
import { ArrayUtils } from '@colonist/utils';
import { usePlayerValidators } from '../../store/selectors/usePlayerValidators.ts';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { UIGameTradeCreatorActionButtons } from './UIGameTradeCreatorActionButtons/index.ts';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
import { useOfferedCardsSubsetOfInventory } from './useOfferedCardsSubsetOfInventory.ts';
export const UIGameTradeCreator = ({ className, isPortraitLayout = false })=>{
    const gameState = useGameStore((state)=>state.gameState);
    const gameSettings = useGameStore((state)=>state.gameSettings);
    const myColor = useGameStore((state)=>state.myColor);
    const { playerValidators, getPlayerValidator } = usePlayerValidators();
    const gameMode = useGameStore((state)=>state.gameMode);
    const wantedCards = useGameStore((state)=>state.gameTrade.wantedCards);
    const setWantedCards = useGameStore((state)=>state.gameTrade.setWantedCards);
    const isShowTradePanel = useGameStore((state)=>state.gameTrade.isShowTradePanel);
    const isBankTradeMode = useGameStore((state)=>state.gameTrade.isBankTradeMode);
    const setIsShowTradePanel = useGameStore((state)=>state.gameTrade.setIsShowTradePanel);
    const setIsBankTradeMode = useGameStore((state)=>state.gameTrade.setIsBankTradeMode);
    const setTradeError = useGameStore((state)=>state.gameTrade.setTradeError);
    const counterOffer = useGameStore((state)=>state.gameTrade.counterOffer);
    const offeredCards = useGameStore((state)=>state.gameCardInventory.selectedCards);
    const setOfferedCards = useGameStore((state)=>state.gameCardInventory.setSelectedCards);
    const setOnSelectCardForTrade = useGameStore((state)=>state.gameCardInventory.setOnSelectCardForTrade);
    const diceState = useGameStore((state)=>state.gameState.diceState);
    const isActionBoxOpening = useGameStore((state)=>state.actionBox.actionBoxData != null);
    const myPlayerValidator = getPlayerValidator(myColor);
    const currentStateValidator = useCurrentStateValidator();
    useOfferedCardsSubsetOfInventory();
    useEffect(()=>{
        if (counterOffer) {
            const myCards = [
                ...myPlayerValidator.resourceCards.cards
            ];
            const givingResourceCards = [];
            for (const card of counterOffer.givingResources){
                const removed = ArrayUtils.removeElementFromArray(myCards, card);
                if (removed) {
                    givingResourceCards.push(card);
                }
            }
            setOfferedCards(givingResourceCards);
            setWantedCards(counterOffer.receivingResources);
            setIsShowTradePanel(true);
        }
    }, [
        counterOffer
    ]);
    useEffect(()=>{
        if (!isShowTradePanel) {
            setOfferedCards([]);
            setWantedCards([]);
        }
    }, [
        isShowTradePanel
    ]);
    const removeCardTypesAndAnyCardFromOfferedCards = useCallback((cardTypes)=>{
        const newOfferedCards = [
            ...offeredCards.filter((prevCard)=>!cardTypes.includes(prevCard) && prevCard != CardEnum.AnyResource)
        ];
        setOfferedCards(newOfferedCards);
    }, [
        offeredCards
    ]);
    const removeAnyCardFromWantedCards = useCallback(()=>{
        setWantedCards(wantedCards.filter((prevCard)=>prevCard != CardEnum.AnyResource));
    }, [
        wantedCards
    ]);
    const addCardToOfferedCards = useCallback((card)=>{
        if (offeredCards.includes(CardEnum.AnyResource) && card === CardEnum.AnyResource) {
            return;
        }
        setOfferedCards([
            ...offeredCards,
            card
        ]);
    }, [
        offeredCards
    ]);
    const addMultipleCardsToOfferedCards = useCallback((card, numberOfCardsToAdd)=>{
        const newCards = Array.from({
            length: numberOfCardsToAdd
        }, ()=>card);
        setOfferedCards([
            ...offeredCards,
            ...newCards
        ]);
    }, [
        offeredCards
    ]);
    const removeSingleCardFromOfferedCards = useCallback((card)=>{
        const newOfferedCards = [
            ...offeredCards
        ];
        ArrayUtils.removeElementFromArray(newOfferedCards, card);
        setOfferedCards(newOfferedCards);
    }, [
        offeredCards
    ]);
    const removeMultipleCardsFromOfferedCards = useCallback((card, numberOfCardsToRemove)=>{
        const newOfferedCards = [
            ...offeredCards
        ];
        for(let i = 0; i < numberOfCardsToRemove; i++){
            ArrayUtils.removeElementFromArray(newOfferedCards, card);
        }
        setOfferedCards(newOfferedCards);
    }, [
        offeredCards
    ]);
    const onRemoveCardFromWantedCards = useCallback((card)=>{
        const newWantedCards = [
            ...wantedCards
        ];
        ArrayUtils.removeElementFromArray(newWantedCards, card);
        setWantedCards(newWantedCards);
    }, [
        wantedCards
    ]);
    const onRemoveCardFromOfferedCards = useCallback((card)=>{
        if (!isBankTradeMode) {
            removeSingleCardFromOfferedCards(card);
            return;
        }
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState;
        var _bankTradeRatios_card;
        const tradeRatio = (_bankTradeRatios_card = bankTradeRatios[card]) !== null && _bankTradeRatios_card !== void 0 ? _bankTradeRatios_card : TradeUtils.DEFAULT_BANK_TRADE_RATIO;
        const cardCount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card);
        let numberOfCardsToRemove = cardCount % tradeRatio;
        numberOfCardsToRemove = numberOfCardsToRemove === 0 ? tradeRatio : numberOfCardsToRemove;
        removeMultipleCardsFromOfferedCards(card, numberOfCardsToRemove);
    }, [
        isBankTradeMode,
        removeSingleCardFromOfferedCards,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        removeMultipleCardsFromOfferedCards
    ]);
    const onAddCardToOfferedCards = useCallback((card)=>{
        if (!diceState.diceThrown) return;
        const myResourceCards = [
            ...myPlayerValidator.resourceCards.cards,
            CardEnum.AnyResource
        ];
        const isNotResourceCard = !myResourceCards.includes(card);
        if (isNotResourceCard) return;
        if (!isShowTradePanel) setIsShowTradePanel(true);
        if (!isBankTradeMode) {
            addCardToOfferedCards(card);
            return;
        }
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState;
        var _bankTradeRatios_card;
        const requiredCardAmount = (_bankTradeRatios_card = bankTradeRatios[card]) !== null && _bankTradeRatios_card !== void 0 ? _bankTradeRatios_card : TradeUtils.DEFAULT_BANK_TRADE_RATIO;
        const currentCardAmountInHand = myPlayerValidator.resourceCards.getCardCountForCard(card);
        const offeredCardAmount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card);
        const canAffordRatio = currentCardAmountInHand - offeredCardAmount >= requiredCardAmount;
        if (!canAffordRatio) {
            setIsBankTradeMode(false);
            setTradeError({
                error: {
                    key: 'strings:game.errors.tradeErrors.invalidBankTradeRatios'
                },
                payload: {
                    hasBankTradeRatioError: true
                }
            });
            addCardToOfferedCards(card);
            return;
        }
        addMultipleCardsToOfferedCards(card, requiredCardAmount);
    }, [
        myPlayerValidator.resourceCards,
        isShowTradePanel,
        isBankTradeMode,
        addCardToOfferedCards,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        addMultipleCardsToOfferedCards,
        diceState.diceThrown
    ]);
    useEffect(()=>{
        setOnSelectCardForTrade(onAddCardToOfferedCards);
    }, [
        onAddCardToOfferedCards
    ]);
    const onAddCardToWantedCards = useCallback((card)=>{
        if (wantedCards.includes(CardEnum.AnyResource) && card === CardEnum.AnyResource) {
            return;
        }
        const newWantedCards = [
            ...wantedCards
        ];
        newWantedCards.push(card);
        setWantedCards(newWantedCards);
    }, [
        wantedCards
    ]);
    const getCurrentTradeCreationData = useCallback((isBankTrade)=>({
            creator: myColor,
            isBankTrade: isBankTrade,
            counterOfferInResponseToTradeId: counterOffer === null || counterOffer === void 0 ? void 0 : counterOffer.tradeId,
            offeredResources: offeredCards,
            wantedResources: wantedCards
        }), [
        offeredCards,
        wantedCards,
        myColor,
        isBankTradeMode,
        counterOffer
    ]);
    const tradeCreationDataValidator = useMemo(()=>{
        const activeResourceCardTypes = gameMode.activeResourceCardTypes;
        const numberOfPlayers = playerValidators.length;
        const myPlayerCards = myPlayerValidator.resourceCards.cards;
        const isTradeCreatorBot = false;
        const bankValidator = new BankValidator(gameState.bankState);
        const myBankTradeRatios = myPlayerValidator.bankTradeRatiosState;
        const activeOffers = gameState.tradeState.activeOffers;
        const closedOffers = gameState.tradeState.closedOffers;
        const modeSetting = gameSettings.modeSetting;
        const embargoState = gameState.tradeState.embargoState;
        const currentTurnPlayerColors = currentStateValidator.currentTurnPlayers;
        return new TradeCreationDataValidator(activeResourceCardTypes, numberOfPlayers, myPlayerCards, isTradeCreatorBot, bankValidator, myBankTradeRatios, activeOffers, closedOffers, modeSetting, embargoState, currentTurnPlayerColors);
    }, [
        gameState,
        gameSettings,
        gameMode,
        playerValidators,
        myColor,
        myPlayerValidator,
        currentStateValidator
    ]);
    const removeCardsIfInvalidBankRatio = useCallback(()=>{
        const tradeCreationData = getCurrentTradeCreationData(true);
        const bankTradeError = tradeCreationDataValidator.getTradeCreationDataError(tradeCreationData);
        if (bankTradeError == null) return;
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState;
        const cardTypesToRemove = [];
        for (const card of offeredCards){
            const cardCount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card);
            var _bankTradeRatios_card;
            const ratioForCard = (_bankTradeRatios_card = bankTradeRatios[card]) !== null && _bankTradeRatios_card !== void 0 ? _bankTradeRatios_card : TradeUtils.DEFAULT_BANK_TRADE_RATIO;
            const isValidBankTradeRatio = cardCount % ratioForCard == 0;
            if (isValidBankTradeRatio) continue;
            cardTypesToRemove.push(card);
        }
        removeCardTypesAndAnyCardFromOfferedCards(cardTypesToRemove);
        removeAnyCardFromWantedCards();
    }, [
        getCurrentTradeCreationData,
        tradeCreationDataValidator,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        removeCardTypesAndAnyCardFromOfferedCards,
        removeAnyCardFromWantedCards
    ]);
    useEffect(()=>{
        if (isBankTradeMode && isShowTradePanel) {
            removeCardsIfInvalidBankRatio();
        }
    }, [
        isBankTradeMode,
        isShowTradePanel
    ]);
    useEffect(()=>{
        if (isActionBoxOpening) {
            setIsShowTradePanel(false);
        }
    }, [
        isActionBoxOpening
    ]);
    const inventoryBackground = useMemo(()=>{
        if (!isPortraitLayout) return 'beige';
        return isShowTradePanel ? 'beige' : 'dark';
    }, [
        isPortraitLayout,
        isShowTradePanel
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.container, className, {
            [styles.portraitLayout]: isPortraitLayout
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.tradeCreatorContainer
    }, isShowTradePanel && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(UIGameTradeCreatorWantedCardSelector, {
        onAddCard: onAddCardToWantedCards,
        isPortraitLayout: isPortraitLayout
    }), /*#__PURE__*/ React.createElement(UIGameTradeCreatorProposal, {
        offeredCards: offeredCards,
        wantedCards: wantedCards,
        onClickOfferedCard: onRemoveCardFromOfferedCards,
        onClickWantedCard: onRemoveCardFromWantedCards,
        isPortraitLayout: isPortraitLayout
    })), /*#__PURE__*/ React.createElement(UIGamePlayerCardInventory, {
        background: inventoryBackground,
        disableTooltip: isPortraitLayout,
        disableCardScaleOnHover: isPortraitLayout
    })), isShowTradePanel && /*#__PURE__*/ React.createElement(UIGameTradeCreatorActionButtons, {
        isPortraitLayout: isPortraitLayout,
        tradeCreationDataValidator: tradeCreationDataValidator,
        getCurrentTradeCreationData: getCurrentTradeCreationData
    }));
};
